import React, { useState, useRef, useMemo } from 'react';
import { history, useSelector } from 'umi';
import { useAsyncEffect } from 'ahooks';
import { Card, Justify, PagingQuery } from 'tea-component';
import Download from '@intelligence/components/ExportTable';
import SearchRecent from '@intelligence/components/SearchRecent';
import Filter from '@intelligence/pages/TopCharts/components/CommonFilter';
import OverlapTable from '@intelligence/pages/OverlapAnalysis/components/Tables';
import useFilterExtend from '@intelligence/hooks/useFilterExtend';
import useGameDetailsLazyData from '@intelligence/v3/_hooks/useGameDetailsLazyData';
import MobileOverlapTopChartsService from './adapter';
import { getFilterName, getFilterValueStorage } from '@intelligence/components/Filter/util';
import { getDbiFilterParams } from '@intelligence/utils/filterUtils';
import { transMetricDataGamesForSearch } from '@intelligence/v3/utils/topcharts';
import { handleSensorTowerValueFilter } from '@intelligence/utils/topchartsUtils';
import { getCrossGameIds } from './utils';
import { SortType } from '@home/enums/common';
import { DATASOURCE_KEY_MAP, SENSORTOWER_METRIC_KEY_MAP } from '@intelligence/v3/_config/metrics/keyMap';
import { FILTER_EXTEND_NAMESPACE } from '@intelligence/models/filterExtend';
import { HOT_TAGS_SOURCE } from '@intelligence/components/Filter/FilterBase/_config';


import type { SortByItemType } from '@intelligence/components/TableComponents/components/SortBy';
import type { ICommonFilterInstance } from '@intelligence/pages/TopCharts/components/CommonFilter';

const ageSortList: any[] = [];

const overlapAnalysisTopchartMobileRankList = (params?: any, isSearchMode?: boolean): IBaseResponse => {
  return MobileOverlapTopChartsService.fetchMobileOverlapTopChartsList(params, isSearchMode);
};

interface IOverlapAnalysisContextProps extends IBaseProps {
  pageLogInfo: any;
  filterCfg: any;
  filterLoading: boolean;
  allMarkets: any[];
  rangeTimeComponentDefault: any;
  rangePickerDisabledMap?: IDateRangeDisabledCallbackDateTypeMap | null;
  downloadLogConfigKey: string;
  /** hotTag 解析后的筛选参数，用于替代 analysisFilterParams */
  hotTagFilterParams?: any;
  /** 下载文件名前缀（选中 hotTag 时使用 name_cn/name_en） */
  downloadFileNamePrefix?: string;
  /** HotTagsSource 选项变化回调（更新 content 层的 selectedHotTagOption） */
  onHotTagConfirm?: (option: any) => void;
}

const OverlapAnalysisContext: React.FC<IOverlapAnalysisContextProps> = (props) => {
  const {
    filterLoading: marketLoading,
    rangeTimeComponentDefault,
    rangePickerDisabledMap,
    filterCfg,
    pageLogInfo,
    downloadLogConfigKey,
    hotTagFilterParams,
    downloadFileNamePrefix,
    onHotTagConfirm,
  } = props;

  const DEFAULT_FORM_VALUE = filterCfg?.defaultValue;

  // filterRef 用于 HotTagsSource 选项变化时调用 setValueAndConfirm
  const filterRef = useRef<ICommonFilterInstance | null>(null);

  // 注入 onHotTagConfirm 到 specialParams，供 CommonFilter → HotTagsSource 使用
  const filterCfgWithHotTag = useMemo(() => ({
    ...filterCfg,
    specialParams: {
      ...filterCfg?.specialParams,
      onHotTagConfirm: async (option: any) => {
        if (!option?.value) return;
        onHotTagConfirm?.(option);
        // HotTagsSource 选中后直接更新页面筛选器值，真正请求交给确认按钮
        const { parseUrlFilterParams } = await import('@intelligence/v3/utils/filterTagNameAnalyser');
        const parsedValue = await parseUrlFilterParams(option.value, {});
        if (parsedValue && Object.keys(parsedValue).length > 0) {
          const nextFilterValue = {
            ...DEFAULT_FORM_VALUE,
            ...parsedValue,
            [HOT_TAGS_SOURCE]: { value: option },
          };

          setFilterVale(nextFilterValue);
          filterRef.current?.setFormValue(nextFilterValue);
        }
      },
    },
  }), [filterCfg, onHotTagConfirm]);

  const { filterExtend, setFilterExtend } = useFilterExtend({});

  // 游戏详情数据
  const {
    loading: gameDetailsMapLoading,
    data: gameDetailsMap,
    setData: setGameDetailsMap,
    refresh: refreshGameDetailsMap,
  } = useGameDetailsLazyData();

  // Cross App Usage、Cross Affinity 游戏详情t数据
  const {
    loading: crossGameDetailsMapLoading,
    data: crossGameDetailsMap,
    setData: setCrossGameDetailsMap,
    refresh: refreshCrossGameDetailsMap,
  } = useGameDetailsLazyData({
    fields: ['entity_name', 'cover'],
  });

  const { userInfo } = useSelector<any, any>((state) => {
    const { userInfo } = state?.home || {};
    return {
      userInfo,
    };
  });

  const filterState = useSelector<any, any>((state) => state?.[FILTER_EXTEND_NAMESPACE] || {});
  const isOperate = filterState?.isOperate;

  const [operatedGame, setOperatedGame] = useState(false);

  // 在筛选器这里拿到缓存的值，并渲染
  const filterName = getFilterName(userInfo);
  const filterData = getFilterValueStorage(userInfo);
  const [filterValue, setFilterVale] = useState(isOperate ? filterExtend : DEFAULT_FORM_VALUE);

  const [tableLoading, setTableLoading] = useState(true);
  const [totalForClearSearch, setTotalForClearSearch] = useState(0);
  const [tableListForClearSearch, setTableListForClearSearch] = useState<any[]>([]);
  const [gameDetailsMapForClearSearch, setGameDetailsMapForClearSearch] = useState<IRecord<IRecord>>({});
  const [crossGameDetailsMapForClearSearch, setCrossGameDetailsMapForClearSearch] = useState<IRecord<IRecord>>({});

  const [tableTotal, setTableTotal] = useState(0);
  const [tableData, setTableData] = useState<any[]>([]);

  const [fetchParams, setFetchParams] = useState({
    entity_type: 'mobile',
    region_type: 'market',
    markets: ['us'],
    order: SortType.Desc,
    order_metric: SENSORTOWER_METRIC_KEY_MAP.ACTIVE_USERS,
    order_source: DATASOURCE_KEY_MAP.SENSORTOWER,
    ratio_type: 0,
    page: 1,
    page_size: 100,
    company: [],
    start_release_time: null,
    end_release_time: null,
    search_by_name: '',
    genre_source: 'iegg',
    iegg_genre_req: [],
    search_game_code: [],
    platforms: ['appstore', 'googleplay'],
    date_type: 'monthly',
    start_date: '',
    end_date: '',
    dateRange: [],
    iegg_genre: [],
    sensortower_genre: [],
    appannie_genre: [],
    mobile_overlap: {
      opt: '',
      value: Number.NaN,
    },
    sensortower_tag_filter: [],
  });

  const getGameData = async (fetchParams: any, isCheckOrSearch: boolean, isSearch = false) => {
    const params = {
      ...fetchParams,
      page: isSearch ? 1 : fetchParams.page,
      search_game_code: isSearch ? fetchParams?.search_game_code || [] : [],
    };
    try {
      setTableData([]);
      setFetchParams(params);
      const res = await overlapAnalysisTopchartMobileRankList(params, isSearch);
      const resData = res?.data || {};
      const total = resData?.total ?? 0;
      const list = resData?.list || [];

      const tableList = list || [];
      const tableListLength = total ?? tableList?.length ?? 0;

      if (!isSearch) {
        setTotalForClearSearch(tableListLength);
        setTableListForClearSearch(tableList);
      }

      const req = res?.req || {};
      const startDate = req?.start_date || '';
      const endDate = req?.end_date || '';
      if (!!startDate && !!endDate) {
        const newFetchParams = {
          ...params,
          start_date: startDate,
          end_date: endDate,
        };
        setFetchParams(newFetchParams);
      }
      setTableTotal(tableListLength);
      setTableData(tableList);

      // 拉取游戏详情数据
      const gameIds = tableList?.map((tableItem: any) => tableItem?.game_id) || [];
      const newGameDetailsMap = await refreshGameDetailsMap(gameIds);

      // 拉取Cross App Usage、Cross Affinity游戏详情数据
      const crossGameIds = getCrossGameIds(tableList);
      const newCrossGameDetailsMap = await refreshCrossGameDetailsMap(crossGameIds);

      if (!isSearch) {
        setGameDetailsMapForClearSearch(newGameDetailsMap);
        setCrossGameDetailsMapForClearSearch(newCrossGameDetailsMap);
      }
    } catch (e) {
      console.error(e);
      setTableTotal(0);
      setTableData([]);
    } finally {
      setTableLoading(false);
    }
  };

  const onTableSortChage = (e: SortByItemType) => {
    const newFetchParams = {
      ...fetchParams,
      order: e?.sortVal,
      order_metric: e?.sortMetric,
      order_source: e?.orderSource,
      ratio_type: e?.ratioType ?? 0,
      page: 1,
      page_size: 100,
    };
    setTableLoading(true);
    getGameData(newFetchParams, operatedGame);
  };

  const onTablePagingChange = (e: PagingQuery) => {
    const newFetchParams = {
      ...fetchParams,
      page: e?.pageIndex || 1,
      page_size: e?.pageSize || 100,
    };
    setTableLoading(true);
    getGameData(newFetchParams, operatedGame);
  };

  // 表格列表项选择事件回调
  const onTableCheckGame = () => {};

  const handleConfirm = async (value: any) => {
    const newFormValue = {
      ...fetchParams,
      ...value,
      page: 1,
      page_size: 100,
    };
    // 请求接口
    setTableLoading(true);
    /** 修改筛选器后，游戏应该重置为未勾选状态 */
    setOperatedGame(false);
    await getGameData(newFormValue, false);
  };

  const handleClearSearch = () => {
    setFetchParams({ ...fetchParams, search_game_code: [] });
    setTableData(tableListForClearSearch);
    setTableTotal(totalForClearSearch);
    setGameDetailsMap(gameDetailsMapForClearSearch);
    setCrossGameDetailsMap(crossGameDetailsMapForClearSearch);
  };

  const getTableSearchList = (id: string) => {
    // 兼容历史数据的搜索记录，最新实现仅取unified_id即可
    const newId = id?.split('game_id')[0];
    setTableLoading(true);
    const searchGameCodes = !newId ? [] : [newId];
    getGameData({ ...fetchParams, search_game_code: searchGameCodes }, true, true);
  };

  useAsyncEffect(async () => {
    setTableLoading(true);
    if (!userInfo?.userid || marketLoading) {
      // 拿缓存值需要拿到用户id
      return;
    }
    // 使用 content 层传入的 hotTagFilterParams（替代原 analysisFilterParams 调用）
    const urlFilterParams = hotTagFilterParams || {};
    const urlFetchParams = handleSensorTowerValueFilter(urlFilterParams);

    let newFetchParams = { ...fetchParams, ...urlFetchParams };

    const { filterValue, filterParams } = getDbiFilterParams({
      // urlFilter: any,
      filterSaveData: filterData?.apiValue,
      filterExtend,
      specialParams: filterCfg?.specialParams,
      isOperate,
      filterName,
      filterKeyList: filterCfg?.filterKeyList ?? Object.keys(DEFAULT_FORM_VALUE),
    });
    if (isOperate) {
      // 如果目前是继承模式，就设置继承值为过滤后的筛选器值
      setFilterExtend(filterValue);
    }
    setFilterVale({ ...filterValue, ...urlFilterParams });
    newFetchParams = { ...newFetchParams, ...filterCfg?.handleFilterToApiParams?.(filterParams) };

    getGameData(newFetchParams, false);
  }, [userInfo?.userid, marketLoading, hotTagFilterParams]);

  return (
    <>
      <Card>
        <Card.Body>
          <Filter
            ref={filterRef}
            filterCfg={filterCfgWithHotTag}
            rangeTimeComponentDefault={rangeTimeComponentDefault}
            rangePickerDisabledMap={rangePickerDisabledMap}
            timeFilterValues={{
              type: 'monthly',
              value: [fetchParams.start_date, fetchParams.end_date],
            }}
            defaultValue={DEFAULT_FORM_VALUE}
            filterValue={filterValue}
            setFilterVale={setFilterVale}
            filterConfirm={handleConfirm}
            filterReset={() => {}}
          />
        </Card.Body>
      </Card>
      <Justify
        className="dbi-card-justify"
        right={
          <>
            <SearchRecent
              searchType="overlap-analysis-mobile"
              fetchParams={{ ...fetchParams, use_custom_fields: ['publisher', 'publisher_country_abbre'], page: 1 }}
              pageLogInfo={pageLogInfo}
              transTableDataForSearch={transMetricDataGamesForSearch}
              getTableResultList={getTableSearchList}
              searchListApi={(params: any) => {
                return overlapAnalysisTopchartMobileRankList(params, true);
              }}
              initSearchInput={!fetchParams?.search_game_code?.length}
              handleClear={handleClearSearch}
            />
            <Download
              useWebDownload
              isShowExportAllDataTip
              entityType="mobile"
              businessType="overlap-mobile-games"
              downloadKey="mobileOverlapTopCharts"
              downloadLogConfigKey={downloadLogConfigKey}
              newStyleFileName={downloadFileNamePrefix
                ? `Hot Tags_${downloadFileNamePrefix}_Overlap Analysis_Mobile Game`
                : "Overlap Analysis_Mobile Game"
              }
              downloadModalSize={820}
              isDisableDownload={tableLoading || marketLoading}
              total={tableTotal}
              tableParams={{ ...fetchParams, regionOptions: filterCfg?.specialParams?.regionOptions || [] }}
              regionOptions={filterCfg?.specialParams?.regionOptions || []}
            />
          </>
        }
      />
      <OverlapTable
        entityType="mobile"
        loading={tableLoading}
        fetchParams={fetchParams}
        genreSource={fetchParams?.genre_source || 'annie'}
        ageSortList={ageSortList}
        tableTotal={tableTotal}
        records={tableData}
        tableSelection={[]}
        tableSelectionAll={[]}
        gameDetailsMapLoading={gameDetailsMapLoading}
        gameDetailsMap={gameDetailsMap}
        crossGameDetailsMapLoading={crossGameDetailsMapLoading}
        crossGameDetailsMap={crossGameDetailsMap}
        onTableCheckGame={onTableCheckGame}
        onTablePagingChange={onTablePagingChange}
        onTableSortChage={onTableSortChage}
        pageSortAttr={{
          tablePageIndex: fetchParams?.page,
          tablePageSize: fetchParams?.page_size,
          currentSortValue: fetchParams?.order,
          currentSortMetric: fetchParams?.order_metric,
          currentOrderSource: fetchParams?.order_source,
          currentRatioType: fetchParams?.ratio_type,
        }}
      />
    </>
  );
};

export default OverlapAnalysisContext;
